// The one place a screen is allowed to fail without taking the console with it.
//
// WRITTEN THE DAY A DASHBOARD WENT WHITE. The console reached the browser ahead
// of the service that sends a field it had started reading, a render threw on
// undefined, and React did what React does with an uncaught throw: it unmounted
// the entire tree. Not the section. Not the page. The rail, the header, the sign
// out, all of it, replaced by nothing. The activation looked like it loaded and
// then the window was blank, and there was no route out except the back button.
//
// THE FAILURE IS THE POINT, NOT THE FIX. Guarding the field that threw is a fix
// for one field. What made a small mistake into a total one is that nothing on
// this console caught anything, so every screen was one bad property access away
// from the same white page. This is that missing floor.
//
// Deliberately wrapping the stage and not the whole app. A boundary around the
// root would catch the same throws and leave the reader in the same place: an
// empty window with nothing to press. Wrapped here, the chrome outlives the
// screen inside it, which means the rail is still there and leaving is a click.
// A reader who can navigate away from a broken screen has had an annoyance. A
// reader who cannot has had an outage.
//
// A class, because this is the one thing hooks still cannot do: there is no
// useErrorBoundary, and componentDidCatch has no functional equivalent.

import { Component, type ReactNode } from "react";

export type StageProps = {
  readonly children: ReactNode;
  /**
   * Where the reader is. Changing it clears a caught error.
   *
   * WITHOUT THIS THE BOUNDARY IS A TRAP. Caught state persists until something
   * resets it, so a boundary that never resets turns one screen's bad render
   * into a permanent refusal: the reader clicks Dashboard, the route changes,
   * the boundary is still holding an error from Kiosk, and every screen after
   * it shows Kiosk's failure. The whole value of keeping the rail alive is that
   * the reader can leave, which requires that arriving somewhere else works.
   */
  readonly at: string;
};

type StageState = {
  readonly failed: Error | null;
  /** The `at` the error belongs to, so a new one can be told from the old. */
  readonly from: string | null;
};

export class Stage extends Component<StageProps, StageState> {
  override state: StageState = { failed: null, from: null };

  static getDerivedStateFromError(failed: Error): Partial<StageState> {
    return { failed };
  }

  static getDerivedStateFromProps(props: StageProps, state: StageState): Partial<StageState> | null {
    // Two jobs in one comparison. A caught error records where it happened on
    // the render after it is caught, and any later render at a different place
    // clears it. Done here rather than in an effect because an effect runs after
    // the paint, which is one frame of the old failure on the new screen.
    if (state.failed !== null && state.from === null) return { from: props.at };
    if (state.failed !== null && state.from !== props.at) return { failed: null, from: null };
    return null;
  }

  // NO componentDidCatch, AND THE ABSENCE IS THE DECISION. The obvious thing to
  // do with a caught error is log it, and this package does not log. That is not
  // a style rule: the check that enforces it says a token in a log line is the
  // failure the whole session-store argument is about, so there is no console
  // logging here at all rather than a rule about what may be logged. An error
  // caught mid-render is exactly the object most likely to be carrying one,
  // because the thing that threw was usually holding a response.
  //
  // So the component stack goes nowhere, and that is a real cost. What is left
  // is the message on the screen, which a reader can quote. Somewhere to send
  // these properly is worth building; a console.error here is not that, it is a
  // credential in the browser's log where any extension can read it.

  override render(): ReactNode {
    const { failed } = this.state;
    if (failed === null) return this.props.children;

    return (
      <div className="panel panel-stack" role="alert">
        {/* A bare h3, which is what .panel styles and what the shared refusal
            state in desk-ui heads itself with. page-h was a class of my own
            invention and no sheet had a rule for it, so this heading drew at
            the browser's default size on the one screen nobody sees until
            something has already gone wrong. */}
        <h3>Something went wrong on this screen</h3>
        <p>
          The rest of Console still works. Use the menu to leave this page.
        </p>
        <p className="help">
          This display error did not save or send anything. If it happens again, report the message below.
        </p>
        {/* The message and not the stack. A reader cannot act on a stack, and a
            stack is what turns a sentence they might quote accurately into a
            wall they will screenshot badly. The stack went to the browser
            console above, where somebody holding the source can reach it. */}
        <p className="help mono">{failed.message}</p>
      </div>
    );
  }
}
